import { InfoCallout } from '@/components/InfoCallout/InfoCallout';
import { useAmelioTranslations } from '@/hooks';
import { useCompetencyVisibility } from '@/pages/EmployeePortal/hooks/useCompetencyVisibility';
import { usePortalAudience } from '@/pages/EmployeePortal/hooks/usePortalAudience';
import { usePortalEvolution } from '@/pages/EmployeePortal/hooks/usePortalReads';
import {
    areLevelsWithheld,
    readCompetencyView,
} from '@/pages/EmployeePortal/tabs/Summary/competencyView';

/**
 * What a manager reading an employee's portal is told, above it: whose portal it is, and that it
 * shows what that employee sees and nothing more.
 *
 * The reason matters as much as the fact. A portal whose levels wait for their meeting shows a lock
 * where the manager expects the levels they entered themselves; unexplained, that reads as a defect
 * of the page rather than as a decision of the company. So the line naming the wait is added -- and
 * only when the wait is really there, read from the same journey the sections draw it from, so the
 * banner cannot claim a lock the portal below it is not showing.
 *
 * Nothing at all on the reader's own portal, which is the portal as it has always been.
 */
export const ViewedPortalBanner = () => {
    const { t } = useAmelioTranslations();
    // The same list the picker beside it shows, so the two can never name different people, and no
    // second request is made for a name that is already on screen. Whose portal this is comes from
    // there too rather than from the address, which cannot tell the reader's own from a colleague's.
    const { people, viewedEmployeeId } = usePortalAudience();
    const employee = people?.find((person) => person.id === viewedEmployeeId);
    const { data: evolution } = usePortalEvolution();
    const { areCompetenciesShown } = useCompetencyVisibility();

    if (viewedEmployeeId === undefined) {
        return null;
    }

    // The lock is the company's choice before it is this employee's situation: a company that never
    // withholds its levels draws no lock, whatever the journey says.
    const isLockShown =
        areCompetenciesShown === true &&
        evolution !== undefined &&
        areLevelsWithheld(readCompetencyView(evolution));

    return (
        <InfoCallout
            // The banner waits for nobody: it says what it knows now, and names the employee as
            // soon as the list answers. An employee the list does not carry is one the reader's
            // scope does not reach, and the portal below says so in its own way.
            title={
                employee
                    ? t('employeePortal.viewingPortal.named', { name: employee.name })
                    : t('employeePortal.viewingPortal.unnamed')
            }
            description={isLockShown ? t('employeePortal.viewingPortal.withheldLevels') : undefined}
            className={'px-4 py-6'}
            dataTestId={'employee-portal-viewing-banner'}
        />
    );
};
